import { useEffect, useRef, useState } from 'react';
import type { LatLng } from '@kick/types';
import { api } from './api';

export type PrecisionGeocodificacion = 'interseccion' | 'via' | 'lugar';

export interface ResultadoGeocodificacion {
  location: LatLng | null;
  precision: PrecisionGeocodificacion | null;
  label: string | null;
}

export type EstadoUbicacion =
  | { tipo: 'inactivo' }
  | { tipo: 'buscando' }
  | { tipo: 'ubicada'; precision: PrecisionGeocodificacion; label: string | null }
  | { tipo: 'no-encontrada' };

/**
 * Al escribir la dirección, el mapa la busca solo.
 *
 * Antes había que escribirla y además ir al mapa a poner el pin: dos veces
 * la misma información. Ahora, medio segundo después de dejar de escribir
 * (o de pegar), el servidor la resuelve y el pin salta al cruce. El mapa
 * sigue siendo la última palabra: hacer clic o arrastrar el pin manda sobre
 * lo que se encontró, hasta que la dirección cambie otra vez.
 *
 * `cerca` desempata: «Calle 10 # 43» existe en Medellín y en Envigado, y se
 * elige la más próxima a donde está el mapa ahora.
 */
export function useUbicacionAutomatica(
  direccion: string,
  onUbicar: (punto: LatLng) => void,
  cerca?: LatLng,
  /** La dirección con la que abrió el formulario: ya tiene su punto guardado, no se busca. */
  omitir?: string,
): EstadoUbicacion {
  const [estado, setEstado] = useState<EstadoUbicacion>({ tipo: 'inactivo' });
  const ultimaBuscada = useRef(omitir?.trim() ?? '');
  const onUbicarRef = useRef(onUbicar);
  onUbicarRef.current = onUbicar;
  const cercaRef = useRef(cerca);
  cercaRef.current = cerca;

  useEffect(() => {
    const texto = direccion.trim();
    // Menos de esto no es una dirección todavía; y la misma no se busca dos veces.
    if (texto.length < 6 || texto === ultimaBuscada.current) return;

    const control = new AbortController();
    const reloj = setTimeout(async () => {
      ultimaBuscada.current = texto;
      setEstado({ tipo: 'buscando' });
      try {
        const punto = cercaRef.current;
        const { data } = await api.get<ResultadoGeocodificacion>('/public/geocodificar', {
          params: { q: texto, ...(punto ? { lat: punto.lat, lng: punto.lng } : {}) },
          signal: control.signal,
        });
        if (data.location && data.precision) {
          onUbicarRef.current(data.location);
          setEstado({ tipo: 'ubicada', precision: data.precision, label: data.label });
        } else {
          setEstado({ tipo: 'no-encontrada' });
        }
      } catch {
        if (!control.signal.aborted) setEstado({ tipo: 'no-encontrada' });
      }
    }, 700);

    return () => {
      clearTimeout(reloj);
      control.abort();
    };
  }, [direccion]);

  return estado;
}

/** El texto que acompaña al mapa según cómo fue la búsqueda. */
export function textoUbicacion(estado: EstadoUbicacion): { texto: string; tono: 'ok' | 'aviso' | 'neutro' } {
  switch (estado.tipo) {
    case 'buscando':
      return { texto: 'Buscando la dirección en el mapa…', tono: 'neutro' };
    case 'ubicada':
      return {
        texto:
          estado.precision === 'interseccion'
            ? `Ubicada en ${estado.label ?? 'la esquina'}. Ajusta el pin a la puerta exacta si hace falta.`
            : estado.precision === 'via'
              ? `Encontramos la vía (${estado.label ?? ''}) pero no el cruce: mueve el pin al punto exacto.`
              : `Ubicada en ${estado.label ?? 'el lugar'}. Ajusta el pin si hace falta.`,
        tono: estado.precision === 'via' ? 'aviso' : 'ok',
      };
    case 'no-encontrada':
      return { texto: 'No encontramos esa dirección: marca el punto en el mapa.', tono: 'aviso' };
    default:
      return { texto: 'Escribe la dirección y la buscamos en el mapa; también puedes hacer clic en él.', tono: 'neutro' };
  }
}
